import { APIClient } from "../../helpers/api_helper";

const api = new APIClient();

const PERMISSIONS_ENDPOINT = "/api/v1/admin/rbac/permissions";

export interface PermissionResponse {
  _id?: string;
  id?: string;
  code: string;
  permission?: string;
  description?: string;
  resource?: string;
  action?: string;
  scopeType?: string;
}

export interface PermissionsMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ListPermissionsResult {
  data: PermissionResponse[];
  meta: PermissionsMeta;
}

export interface ListPermissionsParams {
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * List permissions from the API.
 * Supports server-side search and pagination.
 * Handles responses:
 * - { data: PermissionResponse[], meta: {...} }
 * - { permissions: [] }
 * - { data: [] }
 * - [] (legacy)
 */
export const listPermissions = async (
  params: ListPermissionsParams = {}
): Promise<ListPermissionsResult> => {
  const { search, page = 1, limit = 20 } = params;

  const queryParams: Record<string, string | number> = {
    page,
    limit,
  };
  if (search && search.trim()) {
    queryParams.search = search.trim();
  }

  const queryString =
    "?" +
    Object.entries(queryParams)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join("&");

  const response = await api.get(`${PERMISSIONS_ENDPOINT}${queryString}`, undefined);

  // New shape: { data: [...], meta: {...} }
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    "meta" in response
  ) {
    const typed = response as { data: PermissionResponse[]; meta: PermissionsMeta };
    return {
      data: Array.isArray(typed.data) ? typed.data : [],
      meta: typed.meta,
    };
  }

  // Older shapes: { permissions: [] } or { data: [] } or [].
  let items: PermissionResponse[] = [];
  if (Array.isArray(response)) {
    items = response as PermissionResponse[];
  } else if (response && typeof response === "object") {
    const data = response as unknown as Record<string, unknown>;
    if (Array.isArray(data.permissions)) {
      items = data.permissions as PermissionResponse[];
    } else if (Array.isArray(data.data)) {
      items = data.data as PermissionResponse[];
    }
  }

  return {
    data: items,
    meta: {
      total: items.length,
      page: 1,
      limit: items.length || limit,
      pages: items.length ? 1 : 0,
    },
  };
};
